var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { setJwtToken, setRefreshToken, setContextData, setViewType, isRegistered, setToken, clearAll, setError, } from './authslice';
import { workspaceApiSlice } from '../app/workspaces/service/api';
import NatsClient from './natsclient';
import { expiresIn, isExpired } from '../common/util';
import { DataService } from '../app/configure/service/deviceservice';
import { Constants } from '../app/configure/models/constants';
// Add error keys enum
export var AuthErrorKey;
(function (AuthErrorKey) {
    AuthErrorKey["REFRESH_API_401"] = "REFRESH_API_401";
    AuthErrorKey["REFRESH_API_OTHER"] = "REFRESH_API_OTHER";
    AuthErrorKey["CONTEXT_API_401"] = "CONTEXT_API_401";
    AuthErrorKey["CONTEXT_API_OTHER"] = "CONTEXT_API_OTHER";
})(AuthErrorKey || (AuthErrorKey = {}));
const LOCAL_STORAGE_SMARTLOOK_TENANT_ID = 'smartlook_tenant_id';
const SMARTLOOK_TENANTS_PATH = '/smartlook-tenants.json';
const SMARTLOOK_INIT_SCRIPT_PATH = '/smartlook-init.js';
const SMARTLOOK_INIT_SCRIPT_ID = 'smartlook-init-script';
const SMARTLOOK_BODY_ATTRIBUTE = 'data-sl';
const SMARTLOOK_BODY_ATTRIBUTE_VALUE = 'canvas-mq';
let smartlookTenantsPromise = null;
let smartlookScriptLoadPromise = null;
const getSmartlookTenants = () => __awaiter(void 0, void 0, void 0, function* () {
    if (!smartlookTenantsPromise) {
        smartlookTenantsPromise = fetch(SMARTLOOK_TENANTS_PATH, {
            cache: 'no-store',
        })
            .then((response) => __awaiter(void 0, void 0, void 0, function* () {
            if (!response.ok) {
                throw new Error(`Failed to load Smartlook tenant config: ${response.status}`);
            }
            const payload = (yield response.json());
            const tenants = Array.isArray(payload === null || payload === void 0 ? void 0 : payload.tenants) ? payload.tenants : [];
            return new Set(tenants.map((tenant) => tenant.toString()));
        }))
            .catch((error) => {
            console.warn('Smartlook tenant config unavailable. Skipping Smartlook initialization.', error);
            return new Set();
        });
    }
    return smartlookTenantsPromise;
});
const loadSmartlookInitScript = () => {
    if (smartlookScriptLoadPromise) {
        return smartlookScriptLoadPromise;
    }
    if (typeof document === 'undefined' ||
        document.getElementById(SMARTLOOK_INIT_SCRIPT_ID)) {
        return Promise.resolve();
    }
    smartlookScriptLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.id = SMARTLOOK_INIT_SCRIPT_ID;
        script.src = SMARTLOOK_INIT_SCRIPT_PATH;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => {
            smartlookScriptLoadPromise = null;
            reject(new Error('Failed to load Smartlook init script'));
        };
        document.head.appendChild(script);
    }).catch((error) => {
        console.warn(error);
        throw error;
    });
    return smartlookScriptLoadPromise;
};
const initializeSmartlookForTenant = (tenantId) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    if (typeof window === 'undefined' ||
        typeof document === 'undefined' ||
        tenantId === undefined ||
        tenantId === null) {
        return;
    }
    const normalizedTenantId = tenantId.toString();
    if (!normalizedTenantId) {
        return;
    }
    const allowedTenants = yield getSmartlookTenants();
    if (!allowedTenants.has(normalizedTenantId)) {
        window.localStorage.removeItem(LOCAL_STORAGE_SMARTLOOK_TENANT_ID);
        (_a = document.body) === null || _a === void 0 ? void 0 : _a.removeAttribute(SMARTLOOK_BODY_ATTRIBUTE);
        return;
    }
    window.localStorage.setItem(LOCAL_STORAGE_SMARTLOOK_TENANT_ID, normalizedTenantId);
    (_b = document.body) === null || _b === void 0 ? void 0 : _b.setAttribute(SMARTLOOK_BODY_ATTRIBUTE, SMARTLOOK_BODY_ATTRIBUTE_VALUE);
    yield loadSmartlookInitScript();
});
const authMiddleware = (store) => {
    let intervalId = null;
    const dataService = new DataService();
    return (next) => (action) => __awaiter(void 0, void 0, void 0, function* () {
        const result = next(action);
        const dispatch = store.dispatch;
        const { jwtToken, refreshToken } = store.getState().auth;
        if (!intervalId && jwtToken && refreshToken) {
            console.log('Creating JWT refresh interval - initial tokens available');
            intervalId = setInterval(() => __awaiter(void 0, void 0, void 0, function* () {
                // Get current tokens from store on each interval check
                const currentAuth = store.getState().auth;
                const currentJwtToken = currentAuth.jwtToken;
                const currentRefreshToken = currentAuth.refreshToken;
                if (!currentRefreshToken || isExpired(currentRefreshToken)) {
                    console.error(`Refresh token is expired and refreshToken ${currentRefreshToken ? 'is available' : 'is not available'}`);
                    return;
                }
                if (!currentJwtToken ||
                    expiresIn(currentJwtToken, 60) ||
                    isExpired(currentJwtToken)) {
                    console.log(`JWT token is expired or not available. Going to refresh JWT token. Current token expiry check: expired=${isExpired(currentJwtToken)}, expiresIn60=${expiresIn(currentJwtToken, 60)}`);
                    try {
                        const refreshResult = (yield dispatch(workspaceApiSlice.endpoints.refresh.initiate({
                            jwt: currentJwtToken,
                            refreshToken: currentRefreshToken,
                        })).unwrap());
                        if (refreshResult) {
                            console.log('JWT token refreshed successfully, updating NATS connection...');
                            console.log(`Old JWT token expiry: ${currentJwtToken ? 'available' : 'null'}, New JWT token expiry will be updated`);
                            store.dispatch(setJwtToken(refreshResult.jwt));
                            store.dispatch(setRefreshToken(refreshResult.refreshToken));
                            console.log('Redux state updated with new JWT and refresh tokens');
                            // Update NATS connection with new JWT token and refresh token
                            try {
                                const success = yield NatsClient.updateJWTAndReconnect(refreshResult.jwt, refreshResult.refreshToken);
                                if (success) {
                                    console.log('NATS connection successfully updated with new JWT token');
                                }
                                else {
                                    console.error('Failed to update NATS connection with new JWT token');
                                }
                            }
                            catch (natsError) {
                                console.error('Error updating NATS connection with new JWT token:', natsError);
                            }
                        }
                        else {
                            store.dispatch(setViewType('error'));
                            store.dispatch(setError({
                                message: AuthErrorKey.REFRESH_API_OTHER,
                                showClearCache: true,
                                errorCode: undefined,
                            }));
                        }
                    }
                    catch (error) {
                        const err = error;
                        if (err && err.status === 401) {
                            store.dispatch(setViewType('error'));
                            store.dispatch(setError({
                                message: AuthErrorKey.REFRESH_API_401,
                                showClearCache: true,
                            }));
                        }
                        else {
                            store.dispatch(setViewType('error'));
                            store.dispatch(setError({
                                message: AuthErrorKey.REFRESH_API_OTHER,
                                showClearCache: true,
                                errorCode: err && err.status ? err.status : undefined,
                            }));
                        }
                    }
                }
            }), 
            //5000
            5 * 60 * 1000);
        }
        const act = action;
        if (act.type === isRegistered.type) {
            const registered = act.payload;
            try {
                if (!registered) {
                    console.log('No public key found. Going to register public key');
                    const newPublicKey = NatsClient.getPublicKey();
                    const registerResult = (yield dispatch(workspaceApiSlice.endpoints.register.initiate({
                        publicKey: newPublicKey,
                        tenantMask: dataService.getTenantIdMasked(),
                        deviceId: dataService.getDeviceId(),
                    })).unwrap());
                    console.log('Public key registered successfully :', registerResult);
                    store.dispatch(isRegistered(true));
                }
            }
            catch (error) {
                console.error('Error getting registerResult Data:', error);
            }
        }
        if (act.type === setToken.type) {
            try {
                const token = store.getState().auth.token
                    ? store.getState().auth.token
                    : dataService.getDeviceToken();
                const contextResultUnWrapped = (yield dispatch(workspaceApiSlice.endpoints.context.initiate(token)));
                const contextResult = contextResultUnWrapped === null || contextResultUnWrapped === void 0 ? void 0 : contextResultUnWrapped.data;
                if ('error' in contextResultUnWrapped) {
                    if ((contextResultUnWrapped === null || contextResultUnWrapped === void 0 ? void 0 : contextResultUnWrapped.error) &&
                        'status' in contextResultUnWrapped.error &&
                        contextResultUnWrapped.error.status === 403) {
                        dataService.removeCAuthToken();
                        const encodedRedirectUri = encodeURIComponent(`${window.location.protocol + '//' + `${window.location.host}?token=` + dataService.getDeviceToken()}`);
                        window.location.replace(`${Constants.CENTRAL_AUTH_REDIRECT_URL}&redirect_uri=${encodedRedirectUri}`);
                        return;
                    }
                }
                if (contextResult) {
                    store.dispatch(setContextData(contextResult));
                    void initializeSmartlookForTenant(contextResult.tenantId);
                    // defer viewType until after 'lh' (locationHierarchy) NATS message has populated state.spaces.location.children
                    const connectToServer = () => __awaiter(void 0, void 0, void 0, function* () {
                        const { host, streamName, port, subscriptionInfo, viewType, jwt, refreshToken, } = contextResult;
                        store.dispatch(setJwtToken(jwt));
                        store.dispatch(setRefreshToken(refreshToken));
                        console.log('Going to connect to server with', host, port, streamName);
                        yield NatsClient.connectToNatServer(`${host}:${port}`, jwt, streamName, refreshToken);
                        yield NatsClient.getMessageForSubject(subscriptionInfo.locationHierarchy);
                        const subjects = subscriptionInfo.analytics.concat(subscriptionInfo.locationHierarchy, subscriptionInfo.events);
                        NatsClient.addGlobalSubscriptions(subjects);
                        // Wait until state.spaces.location.children is set (populated by 'lh' message)
                        const checkLocationChildren = () => {
                            const loc = store.getState().spaces.location;
                            return Array.isArray(loc === null || loc === void 0 ? void 0 : loc.children);
                        };
                        const waitForLocationChildren = () => __awaiter(void 0, void 0, void 0, function* () {
                            while (!checkLocationChildren()) {
                                yield new Promise((resolve) => setTimeout(resolve, 20));
                            }
                        });
                        yield waitForLocationChildren();
                        store.dispatch(setViewType(viewType));
                    });
                    // perform connection and then viewType dispatch
                    yield connectToServer();
                }
                else {
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: AuthErrorKey.CONTEXT_API_OTHER,
                        showClearCache: true,
                        errorCode: undefined,
                    }));
                }
            }
            catch (error) {
                const err = error;
                if (err && err.status === 401) {
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: AuthErrorKey.CONTEXT_API_401,
                        showClearCache: true,
                    }));
                }
                else {
                    store.dispatch(setViewType('error'));
                    store.dispatch(setError({
                        message: AuthErrorKey.CONTEXT_API_OTHER,
                        showClearCache: true,
                        errorCode: err && err.status ? err.status : undefined,
                    }));
                }
            }
        }
        if (act.type === clearAll.type) {
            if (intervalId)
                clearInterval(intervalId);
            intervalId = null;
        }
        return result;
    });
};
export default authMiddleware;
